import React from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { Building2, MapPin, TrendingUp, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { Skeleton, Pill } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);

const PROJECT_IMG = (id) => {
  const pool = [
    "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1200&q=80",
  ];
  return pool[(id || 0) % pool.length];
};

export default function MgmtProjects() {
  const { data } = useSWR("/management/projects-overview", fetcher);
  if (!data) return <Skeleton h={400}/>;

  return (
    <div className="space-y-5">
      <div>
        <div className="rc-eyebrow">Portfolio</div>
        <div className="rc-title-1 mt-1">Projects</div>
        <div className="rc-callout">Tower &amp; flat-level breakdown across the Radiance portfolio.</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.map((p, idx) => {
          const bookedPct = p.total_flats > 0 ? Math.round((p.booked / p.total_flats) * 100) : 0;
          return (
            <Link key={p.project_id} to={`/management/projects/${p.project_id}`} className="rc-image-card rc-rise group" style={{ animationDelay: `${idx * 60}ms`, minHeight: 280 }} data-testid={`proj-${p.project_id}`}>
              <div className="rc-image-card-bg" style={{ backgroundImage: `url(${p.image_url || PROJECT_IMG(p.project_id)})`, transition: "transform 600ms ease" }}/>
              <div className="rc-image-card-overlay"/>
              <div className="rc-image-card-content flex flex-col h-full">
                <div className="flex items-start justify-between gap-2">
                  <div className="rc-tag" data-no-shadow><Building2 size={11}/> {p.is_completed ? "Completed" : "Active"}</div>
                  {p.open_tickets > 0 && (
                    <div className="rc-pill" data-no-shadow style={{ background: "rgba(255,149,0,0.95)", color: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>
                      {p.open_tickets} open
                    </div>
                  )}
                </div>
                <div className="mt-3 inline-block self-start">
                  <div className="rc-title-2 text-white" style={{ textShadow: "0 2px 6px rgba(0,0,0,0.85), 0 0 12px rgba(0,0,0,0.6)" }}>{p.project_name}</div>
                  <div className="rc-footnote flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md w-fit" data-no-shadow style={{ background: "rgba(0,0,0,0.55)", color: "#fff", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}>
                    <MapPin size={11} className="text-[#F9A61C]"/>{p.location}
                  </div>
                </div>
                <div className="flex-1 min-h-4"/>
                {/* Stats panel with very dark scrim — guaranteed readable */}
                <div className="rounded-2xl p-3 sm:p-4" data-no-shadow style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", boxShadow: "0 6px 20px rgba(0,0,0,0.35)" }}>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    <div>
                      <div className="uppercase" style={{ color: "#fff", opacity: 0.9, fontSize: 10, fontWeight: 600, letterSpacing: 0.5 }}>Booked</div>
                      <div className="rc-tabular text-white" style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.1 }}>{p.booked}<span style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, fontWeight: 400 }}>/{p.total_flats}</span></div>
                    </div>
                    <div>
                      <div className="uppercase" style={{ color: "#fff", opacity: 0.9, fontSize: 10, fontWeight: 600, letterSpacing: 0.5 }}>Constr.</div>
                      <div className="rc-gradient-text rc-tabular" style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.1 }}>{p.completion_pct}%</div>
                    </div>
                    <div>
                      <div className="uppercase" style={{ color: "#fff", opacity: 0.9, fontSize: 10, fontWeight: 600, letterSpacing: 0.5 }}>Demand</div>
                      <div className="rc-tabular text-white" style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.1 }}>{bookedPct}%</div>
                    </div>
                  </div>
                  <div className="mt-3 h-1.5 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full" style={{ width: `${p.completion_pct}%`, background: "linear-gradient(90deg, #F9A61C, #F27024)" }}/>
                  </div>
                  <div className="mt-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-1" style={{ color: "#fff", fontSize: 12, fontWeight: 500 }}><TrendingUp size={12}/> View tower-level</div>
                    <ArrowUpRight size={16} className="text-white transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"/>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
